import { useState } from "react";
import { t } from "i18next";
import type { NavigateFunction } from "react-router-dom";
import { Ban, Eye } from "lucide-react";

import { RowActionsMenu } from "@/core/components/shared/data-table";
import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCancelCompanySubscription } from "@/features/fee-engine/api/use-cancel-company-subscription";
import { useFeeEngineTranslation } from "@/features/fee-engine/i18n/use-fee-engine-translation";
import type { CompanySubscriptionRow } from "@/features/fee-engine/components/company-subscriptions/company-subscriptions-columns";

/** Menu d'actions de ligne — annulation via ShareablePreview. */
export function CompanySubscriptionRowActions({
  companySubscription,
  navigate: _navigate,
}: {
  companySubscription: CompanySubscriptionRow;
  navigate: NavigateFunction;
}) {
  const { t: tFees } = useFeeEngineTranslation();
  const cancel = useCancelCompanySubscription();
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <RowActionsMenu
        actions={[
          {
            id: "view",
            label: t("fees.subscriptions.actions.view"),
            icon: Eye,
            onClick: () => {
              // Pas de page détail dédiée en phase 5 — hook `useCompanySubscription(id)` disponible.
            },
          },
          ...(companySubscription.status !== "CANCELLED"
            ? [
                {
                  id: "cancel",
                  label: t("fees.subscriptions.actions.cancel"),
                  icon: Ban,
                  variant: "destructive" as const,
                  onClick: () => setConfirmOpen(true),
                },
              ]
            : []),
        ]}
      />

      {confirmOpen ? (
        <ShareablePreview
          data={companySubscription}
          title={tFees("fees.subscriptions.preview.cancelTitle")}
          description={tFees("fees.subscriptions.preview.cancelDescription")}
          mode="user"
          fees={[]}
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !cancel.isPending) setConfirmOpen(false);
          }}
          maxHeight="90vh"
          actions={createPreviewActions({
            onConfirm: () =>
              cancel.mutate(companySubscription.id, {
                onSuccess: () => {
                  notifSilence(tFees("fees.subscriptions.feedback.cancelled"));
                  setConfirmOpen(false);
                },
                onError: (error) => notifSilenceError(error.message),
              }),
            onCancel: () => setConfirmOpen(false),
            t: tFees,
            confirmLoading: cancel.isPending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </>
  );
}
